import { useState, type ReactNode } from 'react';
import { IconCopy, IconRefresh } from './icons.js';

/**
 * Segredo forte e URL-safe (hex): serve inclusive para o path do webhook do
 * NoxPay, onde caractere especial precisaria de escape. 32 bytes = 64 hex.
 */
export function gerarSegredo(bytes = 32): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (b) => b.toString(16).padStart(2, '0')).join('');
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, hint, error, required, children, htmlFor }: FieldProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required && <span className="field__req">*</span>}
      </label>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

interface TextInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  id?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
  inputMode?: 'text' | 'numeric';
}

export function TextInput({
  value,
  onChange,
  placeholder,
  mono,
  id,
  autoFocus,
  onEnter,
  inputMode,
}: TextInputProps) {
  return (
    <input
      id={id}
      autoFocus={autoFocus}
      className={`input${mono ? ' input--mono' : ''}`}
      value={value}
      placeholder={placeholder}
      inputMode={inputMode}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onEnter ? (e) => e.key === 'Enter' && onEnter() : undefined}
    />
  );
}

interface SelectProps {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  id?: string;
}

export function Select({ value, onChange, options, id }: SelectProps) {
  return (
    <select id={id} className="select-native" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((op) => (
        <option key={op} value={op}>
          {op}
        </option>
      ))}
    </select>
  );
}

interface SecretInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Oferece o botão "gerar aleatório" — só para segredos que nós definimos. */
  generate?: boolean;
  id?: string;
}

/**
 * Campo de segredo: mascarado por padrão, com alternar visão, copiar e (quando é
 * um segredo nosso) gerar. O backend nunca devolve segredo salvo, então reabrir
 * mostra vazio — o placeholder diz que em branco preserva o atual.
 */
export function SecretInput({ value, onChange, placeholder, generate, id }: SecretInputProps) {
  const [visivel, setVisivel] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    if (!value) return;
    try {
      await navigator.clipboard?.writeText(value);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* clipboard bloqueado (sem HTTPS/permissão) — nada a fazer */
    }
  };

  return (
    <div className="input-group">
      <input
        id={id}
        className="input input--mono"
        type={visivel ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
      <button type="button" className="btn btn--subtle btn--sm" onClick={() => setVisivel((v) => !v)} title={visivel ? 'Ocultar' : 'Mostrar'}>
        {visivel ? 'ocultar' : 'ver'}
      </button>
      {generate && (
        <button type="button" className="btn btn--subtle btn--icon btn--sm" onClick={() => onChange(gerarSegredo())} title="Gerar aleatório">
          <IconRefresh size={15} />
        </button>
      )}
      {value && (
        <button type="button" className="btn btn--subtle btn--icon btn--sm" onClick={() => void copiar()} title={copiado ? 'Copiado' : 'Copiar'}>
          <IconCopy size={15} />
        </button>
      )}
    </div>
  );
}
